// RepeatPanel — the operator's half of the repeat guard, in one place.
//
// bff/src/routes/transfers.ts refuses a second identical transfer inside 12
// hours (same two accounts, same asset, same amount) with 409
// repeat_suspected. It exists because the operator's 19,999.50 from Loki was
// booked at 09:54 and again at 10:00 off the same chain row — one inbound on
// chain, two receipts in the books.
//
// The refusal is only half the tool. A repeat that really is a second payment
// has to be postable, and the guard answers every transfer, not just the one
// screen that grew a panel for it: Move money and Inter-entity met "This looks
// like a repeat … Check before posting it again" with nothing to press, so a
// legitimate second move could not be booked at all. One hook and one panel,
// used by every screen that posts a transfer.
//
// THE "MONEY" KIND HAS A REMEDY OF ITS OWN (sweep item 39, 14 Sept 2026).
// money_already_recorded means the money is in the books already, so the
// honest next step is usually not "post it again" but "book the trade
// settle-later, and let the entry that is there settle it". A screen that can
// flip its own settle-later switch passes `settleLater.onClick`; Record money,
// which has no such switch, passes `settleLater.to` — a /trade link carrying
// the party, the leg and ?later=1. Either way that becomes the primary button
// and "Post it again anyway" is demoted to a link. Without it the panel keeps
// its two buttons as before.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/errors';
import { fmtRelative } from '../lib/format';

/** WHICH guard refused. The BFF separated the two confirmations on 5 Sept
 *  2026: `confirm_repeat` silences only the twin-trade / twin-transfer guard
 *  (409 repeat_suspected), `confirm_money_moved_again` only the cross-shape
 *  guard (409 money_already_recorded). A screen's "post it again anyway" has
 *  to send the flag for the refusal it actually saw — a yes to "this trade is
 *  a genuine repeat" must not also answer "this money genuinely moved twice". */
export type RepeatKind = 'repeat' | 'money';

export interface RepeatTwin {
  kind: RepeatKind;
  reference: string | null;
  timestamp: string;
  same_chain_transfer?: boolean;
  /** Set when the refusal was `money_already_recorded`: the same money is
   *  already in the books WITHOUT a trade, so a settled trade would record it
   *  twice. The API's own sentence says what to do; the panel shows it. */
  reason?: string;
  /** Set by the transfers-side cross-shape guard (14 Sept 2026): the twin is
   *  a SETTLED TRADE that moved this money itself, not a standalone movement.
   *  Only the headline changes; the remedies are the same. */
  settled?: boolean;
  /** Set by the trade repeat guard's mirror arm (14 Sept 2026, sweep item
   *  15): the twin is the same trade booked the OTHER WAY ROUND — same party,
   *  assets and amounts, opposite direction (7ZPST and P3EGZ were booked the
   *  wrong way round and a direction-bound guard saw neither). The BFF sends
   *  its sentence in `reason`; the headline says which kind of twin it is. */
  mirror?: boolean;
}

export function useRepeatGuard() {
  const [twin, setTwin] = useState<RepeatTwin | null>(null);

  /**
   * Run the posting and turn a 409 into the panel below. The thrown
   * 'repeat_suspected' is the sentinel errors.ts knows to keep off the screen
   * (isSentinel), so the operator sees the twin and the two buttons rather
   * than a red sentence beside them. The twin remembers which refusal made
   * it (`kind`), so a screen's re-POST confirms that one and no other.
   */
  async function guard<T>(post: () => Promise<T>): Promise<T> {
    try {
      return await post();
    } catch (e) {
      const body = e instanceof ApiError && e.status === 409
        ? (e.body as { error?: string; twin?: Omit<RepeatTwin, 'kind'>; reason?: string; settled?: boolean })
        : null;
      if (body?.error === 'repeat_suspected' && body.twin) {
        setTwin({ ...body.twin, kind: 'repeat' });
        throw new Error('repeat_suspected');
      }
      // The same money already recorded WITHOUT a trade — the shape that
      // double-booked 60,000 USDT to HK on 3 Sept — or, from the transfers
      // side, already moved WITH a settled trade (14 Sept). Same panel, its
      // own words: the API's sentence names the twin and the figures.
      if (body?.error === 'money_already_recorded' && body.twin) {
        setTwin({ ...body.twin, kind: 'money', reason: body.reason, settled: body.settled === true });
        throw new Error('repeat_suspected');
      }
      throw e;
    }
  }

  return { twin, clearTwin: () => setTwin(null), guard };
}

/** How a screen offers "Book it settle-later instead" (see the header): a
 *  link to Book trade with the entry's facts prefilled, or a handler that
 *  flips the screen's own settle-later switch and re-posts. */
export type SettleLaterRemedy = { to: string } | { onClick: () => void };

export function RepeatPanel({
  twin,
  pending,
  onAlreadyBooked,
  onPostAnyway,
  settleLater,
}: {
  twin: RepeatTwin;
  pending: boolean;
  /** The twin is the right entry — leave the books alone (usually go back). */
  onAlreadyBooked: () => void;
  /** This really is a second movement — post it again with the confirm that
   *  answers `twin.kind` (confirm_repeat, or confirm_money_moved_again). */
  onPostAnyway: () => void;
  /** The "money" kind's primary remedy. Read only when `twin.kind` is 'money';
   *  absent, the panel keeps its two buttons. */
  settleLater?: SettleLaterRemedy;
}) {
  const bookLater = twin.kind === 'money' ? settleLater : undefined;
  const headline = twin.mirror
    ? 'The same trade the other way round is already booked — nothing was posted.'
    : twin.settled
    ? 'This money already moved with a settled trade — nothing was posted.'
    : twin.reason
      ? 'This money is already in the books — nothing was posted.'
      : 'Looks like a repeat — nothing was posted.';
  return (
    <div className="rounded-lg bg-warn-bg p-3 text-body space-y-2">
      <p className="font-medium text-warn">{headline}</p>
      <p className="text-meta text-tg-hint">
        {twin.reason ?? (
          <>
            {twin.same_chain_transfer
              ? 'This chain transfer is already booked as '
              : 'The same money already went through as '}
            <span className="font-mono">{twin.reference ?? 'an earlier entry'}</span>, {fmtRelative(twin.timestamp)}.
            {twin.same_chain_transfer
              ? ' If that one is right, go back — a chain row clears on Refresh.'
              : ' If that one is right, go back and leave the books as they are.'}
          </>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {bookLater && ('to' in bookLater
          ? (
            <Link to={bookLater.to} className="tap-44 btn-primary text-body py-1.5">
              Book it settle-later instead
            </Link>
          ) : (
            <button type="button" className="tap-44 btn-primary text-body py-1.5" disabled={pending} onClick={bookLater.onClick}>
              Book it settle-later instead
            </button>
          ))}
        <button type="button" className="tap-44 btn-secondary text-body py-1.5" onClick={onAlreadyBooked}>
          It&apos;s already booked
        </button>
        {/* With a settle-later remedy on offer, posting the money a second
            time is the exception and reads as a link; without one it stays
            the button it always was. */}
        <button
          type="button"
          className={`tap-44 ${bookLater ? 'btn-link text-meta text-neg' : 'btn-secondary text-body py-1.5 text-neg'}`}
          disabled={pending}
          onClick={onPostAnyway}
        >
          Post it again anyway
        </button>
      </div>
    </div>
  );
}
